import { useContext, useState } from 'react';
import { Select, Spin } from 'antd';
import styled from '@emotion/styled';
import _ from 'lodash';
import { TPODataContext } from 'contexts/TPOContext';
import { PromoTactic, promoTacticIdNamePair } from 'types/tradePromoOptimization';
import { UserContext } from 'contexts/ConfigContext';
import { formatWord, getConfig, sendNotification } from 'utils/helperMethods';
import CustomTabs from 'components/atoms/CustomTabs/CustomTabs';
import { useCommonApiHandler } from 'hooks/general/useCommonApiHandler';
import { tradePromoOptimizationUrls } from 'config/urls';
import PromoInfoSection from './PromoSections/PromoInfoSection';
import PromoMetricDetails from './PromoSections/PromoMetricDetails';
import { NEW_EVENT_PROMO_ID } from 'config/constants';
import axios from 'axios';
import ConfirmationModal from 'components/molecules/common/modals/ConfirmationModal';
import { antIconDanger } from '../PlanActions';
import useLastUpdatedAtPolling from 'hooks/tradePromoOptimization/promo/useLastUpdatedAtPolling';
import dayjs from 'dayjs';

export const PromoSelect = styled(Select)`
    .ant-select-arrow-loading {
        color: blue !important;
        font-size: 16px;
    }
    .ant-select-selector {
        border-radius: 6px !important;
        border: none !important;
    }
    &.ant-select-multiple .ant-select-selection-item-remove {
        position: relative;
        top: -2px;
    }
`;

export enum MetricsApiTriggerPoints {
    None = 0,
    Date,
    Tactic,
    PPG,
}

type PromosProps = {
    onPromoActiveTabChange: (id: number) => void;
    mechanicType: string;
    eventDataLoading: boolean;
    disabled: boolean;
    eventId: null | string;
};
const Promos = ({ onPromoActiveTabChange, mechanicType, eventDataLoading, disabled, eventId }: PromosProps) => {
    const {
        selectedEventInfo,
        updateSelectedEventProperty,
        promoActiveTab,
        isValid,
        setPromoActiveTab,
        setSelectedPromoPPGIds,
        promoConfigDictionary,
        isTpmTab,
    } = useContext(TPODataContext);
    const { appConfig } = useContext(UserContext);
    const { promoLimit, deleteAllowedStatus = [] } = getConfig('tpo', appConfig);
    const [fetchMetricsTriggeredFrom, setFetchMetricsTriggeredFrom] = useState<MetricsApiTriggerPoints>(
        MetricsApiTriggerPoints.None,
    );
    const [deleteLoading, setDeleteLoading] = useState<boolean>(false);
    const [deletePromoModalInfo, setDeletePromoModalInfo] = useState<{
        id?: string;
        name?: string;
        open?: boolean;
    }>({});

    //get promo details by promo id
    const { loading: activeTabPromoDetailsLoading } = useCommonApiHandler({
        method: 'get',
        queryKey: ['tpo_get_promo_details_by_id', promoActiveTab],
        queryConfig: { enabled: !!promoActiveTab && promoActiveTab !== NEW_EVENT_PROMO_ID, cacheTime: 0, staleTime: 0 }, // Disable caching
        url: `${tradePromoOptimizationUrls.getPromoDetailsByPromoId}/${promoActiveTab}`,
        parserFunction: (res: any) => {
            try {
                if (res) {
                    const updatedPPGs = res.ppgs.map((p: any) => {
                        return {
                            id: p?.lowest_dim_attr_id ?? null,
                            name: p?.lowest_dim_attr_name ?? '',
                            isSelected: true,
                            metrics: Array.isArray(p?.promo_metrics)
                                ? p.promo_metrics.map((m: any) => {
                                      return {
                                          id: m?.key ?? null,
                                          name: promoConfigDictionary.promoMetrics[m.key]?.name,
                                          value: m?.input_value ?? null,
                                          recomValue: m?.recom_value ?? null,
                                          isSelected: m?.is_selected ?? false,
                                      };
                                  })
                                : [],
                            spendDetails: Array.isArray(p?.promo_spend_details)
                                ? p.promo_spend_details.map((m: any) => {
                                      return {
                                          id: m?.key ?? null,
                                          name: promoConfigDictionary.spendMetrics[m.key]?.name,
                                          value: m?.input_value ?? null,
                                          recomValue: m?.recom_value ?? null,
                                          isSelected: m?.is_selected ?? false,
                                      };
                                  })
                                : [],
                        };
                    });

                    const tempPromo: PromoTactic = {
                        name: res.name,
                        promoId: res.event_promo_id,
                        tpmPromoId: res.tpm_promo_id,
                        status: res.new_status,
                        customPromo: res.custom_promo_grouping,
                        promoType: res.promotion_type_id,
                        tacticType: res.tactic_type_id,
                        featureQuality: res.tactic_feature_value,
                        displayQuality: res.tactic_display_value,
                        promoStartDate: res.promo_start_timestamp,
                        promoEndDate: res.promo_end_timestamp,
                        craEndDate: res.cra_end_timestamp,
                        craStartDate: res.cra_start_timestamp,
                        offerType: res.offer_type,
                        offer: res.offer_details.offer_mechanic,
                        manualOffer: res.offer_details.manual_details.type,
                        discount: res.offer_details.manual_details.value,
                        discountUnit: res.offer_details.manual_details.is_value_percentage ? '%' : 'abs',
                        buyGet:
                            res.offer_details.manual_details.buy && res.offer_details.manual_details.get
                                ? [res.offer_details.manual_details.buy, res.offer_details.manual_details.get]
                                : [0, 0],
                        comboUnit: res.offer_details.manual_details.combo_unit ?? 'abs',
                        ppgs: updatedPPGs,
                        isLoading: false,
                        isRefreshRequired: false,
                        lastUpdatedAt: res.last_updated_at,
                        fundingRequest: res.funding_request,
                        approvedDate: res.approved_on_date,
                        contractNumber: res.contract,
                        parNumber: res.par,
                        ismCampaignId: res.ism_campaign_id,
                        buyFor:
                            res.offer_details.manual_details.bf_buy && res.offer_details.manual_details.bf_for
                                ? [res.offer_details.manual_details.bf_buy, res.offer_details.manual_details.bf_for]
                                : [0, 0],
                    };

                    updateSelectedEventProperty('currentPromoTactic', tempPromo);
                }
                if (res?.ppgs?.length > 0) {
                    setSelectedPromoPPGIds(res.ppgs.map((p: any) => p.lowest_dim_attr_id));
                }

                return {};
            } catch (error) {
                throw error;
            }
        },
    });

    //get promo details by promo id for first id
    useCommonApiHandler({
        method: 'get',
        queryKey: ['first_tpo_get_promo_details_by_id', selectedEventInfo?.firstPromoTacticBackup?.promoId],
        queryConfig: { enabled: !!selectedEventInfo?.firstPromoTacticBackup?.promoId, cacheTime: 0, staleTime: 0 }, // Disable caching
        url: `${tradePromoOptimizationUrls.getPromoDetailsByPromoId}/${selectedEventInfo?.firstPromoTacticBackup?.promoId}`,
        parserFunction: (res: any) => {
            try {
                if (res) {
                    const updatedPPGs = res.ppgs.map((p: any) => {
                        return {
                            id: p?.lowest_dim_attr_id ?? null,
                            name: p?.lowest_dim_attr_name ?? '',
                            isSelected: true,
                            metrics: Array.isArray(p?.promo_metrics)
                                ? p.promo_metrics.map((m: any) => {
                                      return {
                                          id: m?.key ?? null,
                                          name: promoConfigDictionary.promoMetrics[m.key]?.name,
                                          value: m?.input_value ?? null,
                                          recomValue: m?.recom_value ?? null,
                                          isSelected: m?.is_selected ?? false,
                                      };
                                  })
                                : [],
                            spendDetails: Array.isArray(p?.promo_spend_details)
                                ? p.promo_spend_details.map((m: any) => {
                                      return {
                                          id: m?.key ?? null,
                                          name: promoConfigDictionary.spendMetrics[m.key]?.name,
                                          value: m?.input_value ?? null,
                                          recomValue: m?.recom_value ?? null,
                                          isSelected: m?.is_selected ?? false,
                                      };
                                  })
                                : [],
                        };
                    });

                    const tempPromo: PromoTactic = {
                        name: res.name,
                        promoId: res.event_promo_id,
                        tpmPromoId: res.tpm_promo_id,
                        status: res.new_status,
                        customPromo: res.custom_promo_grouping,
                        promoType: res.promotion_type_id,
                        tacticType: res.tactic_type_id,
                        featureQuality: res.tactic_feature_value,
                        displayQuality: res.tactic_display_value,
                        promoStartDate: res.promo_start_timestamp,
                        promoEndDate: res.promo_end_timestamp,
                        craEndDate: res.cra_end_timestamp,
                        craStartDate: res.cra_start_timestamp,
                        offerType: res.offer_type,
                        offer: res.offer_details.offer_mechanic,
                        manualOffer: res.offer_details.manual_details.type,
                        discount: res.offer_details.manual_details.value,
                        discountUnit: res.offer_details.manual_details.is_value_percentage ? '%' : 'abs',
                        buyGet:
                            res.offer_details.manual_details.buy && res.offer_details.manual_details.get
                                ? [res.offer_details.manual_details.buy, res.offer_details.manual_details.get]
                                : [0, 0],
                        comboUnit: res.offer_details.manual_details.combo_unit ?? 'abs',
                        ppgs: updatedPPGs,
                        isLoading: false,
                        fundingRequest: res.funding_request,
                        approvedDate: res.approved_on_date,
                        contractNumber: res.contract,
                        parNumber: res.par,
                        ismCampaignId: res.ism_campaign_id,
                        buyFor:
                            res.offer_details.manual_details.bf_buy && res.offer_details.manual_details.bf_for
                                ? [res.offer_details.manual_details.bf_buy, res.offer_details.manual_details.bf_for]
                                : [0, 0],
                    };

                    updateSelectedEventProperty('firstPromoTacticBackup', {
                        ...selectedEventInfo?.firstPromoTacticBackup,
                        promoInfo: tempPromo,
                    });
                }
                if (res?.ppgs?.length > 0) {
                    setSelectedPromoPPGIds(res.ppgs.map((p: any) => p.lowest_dim_attr_id));
                }

                return {};
            } catch (error) {
                throw error;
            }
        },
    });

    // TODO: disabling this feature for now
    // useLastUpdatedAtPolling(selectedEventInfo, updateSelectedEventProperty);

    const handlePromoDelete = (promoId: string, promoName: string) => {
        setDeletePromoModalInfo({ open: true, name: promoName, id: promoId });
    };

    const onPromoDelete = async () => {
        const { id: promoId } = deletePromoModalInfo;
        if (promoId === NEW_EVENT_PROMO_ID) {
            updatePostDel(promoId);
        } else {
            setDeleteLoading(true);
            await axios
                .delete(`${tradePromoOptimizationUrls.deletePromoUrl}/${promoId}`)
                .then(() => {
                    sendNotification('success', 'Promo deleted successfully');
                    updatePostDel(promoId as string);
                })
                .catch((err) => {
                    console.error(err);
                    sendNotification('error', 'Something went wrong. Unable to delete');
                })
                .finally(() => {
                    setDeleteLoading(false);
                });
        }
    };

    const updatePostDel = (promoId: string) => {
        const currentTabs = selectedEventInfo?.promoTacticsIdNamePair;
        const newTabs = currentTabs?.filter((tab) => tab.id !== promoId);
        if (newTabs?.length) {
            updateSelectedEventProperty('promoTacticsIdNamePair', newTabs);
            setPromoActiveTab(newTabs[newTabs?.length - 1].id);
        } else {
            updateSelectedEventProperty('promoTacticsIdNamePair', undefined);
            setPromoActiveTab(null);
        }
        setDeletePromoModalInfo({});
    };

    const onAddPromoBtnClick = () => {
        // MAX LIMIT ERROR
        if ((selectedEventInfo?.promoTacticsIdNamePair?.length || 0) >= promoLimit) {
            sendNotification('error', `Upto ${promoLimit} promos can be added`);
            return;
        } else {
            const newName = getNewPromoName(
                selectedEventInfo?.promoTacticsIdNamePair?.map((i: promoTacticIdNamePair) => i.name) || [],
            );

            updateSelectedEventProperty('promoTacticsIdNamePair', [
                ...(selectedEventInfo?.promoTacticsIdNamePair || []),
                { id: NEW_EVENT_PROMO_ID, name: newName },
            ]);

            // if its the first one add default value
            if (selectedEventInfo?.promoTacticsIdNamePair?.length === 0) {
                updateSelectedEventProperty('currentPromoTactic', {
                    name: newName,
                    promoId: NEW_EVENT_PROMO_ID,
                    tpmPromoId: null,
                    status: 'planned', // todo - make it config option to set update with default status property
                    customPromo: null,
                    promoType: null,
                    tacticType: null,
                    promoStartDate: null,
                    promoEndDate: null,
                    ppgs: [],
                    featureQuality: null,
                    displayQuality: null,
                    offerType: null,
                    manualOffer: null,
                    isLoading: false,
                    buyGet: [0, 0],
                    buyFor: [0, 0],
                    discount: null,
                    offer: null,
                    craEndDate: null,
                    craStartDate: null,
                    comboUnit: 'abs',
                    discountUnit: 'abs',
                    lastUpdatedAt: Date.now(),
                });
                setSelectedPromoPPGIds([]);
            } else {
                if (selectedEventInfo?.firstPromoTacticBackup?.promoInfo) {
                    const newPromo = _.cloneDeep(selectedEventInfo.firstPromoTacticBackup.promoInfo);
                    const startDate = newPromo.promoStartDate;

                    newPromo.name = newName;
                    newPromo.promoId = NEW_EVENT_PROMO_ID;
                    newPromo.hasChanges = true;

                    if (startDate && dayjs.unix(startDate).isBefore(dayjs().startOf('day'))) {
                        // If promo start date is in the past
                        newPromo.promoStartDate = null;
                        newPromo.promoEndDate = null;
                        newPromo.craStartDate = null;
                        newPromo.craEndDate = null;
                        newPromo.status = 'planned';
                        (newPromo.tpmPromoId = null), (newPromo.ppgs = []);
                        setSelectedPromoPPGIds([]);

                        sendNotification(
                            'info',
                            'The original promo is in the past. Basic details copied, but dates have been reset. Please configure new dates.',
                        );
                    } else {
                        sendNotification('info', 'Copied from the first Promo');
                    }

                    updateSelectedEventProperty('currentPromoTactic', newPromo);
                }
            }

            onPromoActiveTabChange(0);
            // keep the newly added tab as active tab
            setPromoActiveTab(NEW_EVENT_PROMO_ID);
        }
    };

    function getNewPromoName(existingNames: string[]) {
        // Extract existing promo numbers
        const promoNumbers = existingNames
            .map((name) => {
                const match = name.match(/^Promo (\d+)$/);
                return match ? parseInt(match[1], 10) : null;
            })
            .filter((num) => num !== null);

        // Determine the starting point: length + 1
        let newNumber = existingNames.length + 1;

        // Increment until a unique number is found
        while (promoNumbers.includes(newNumber)) {
            newNumber++;
        }

        // Generate the new promo name
        return `Promo ${newNumber}`;
    }

    return (
        <>
            <CustomTabs
                tabs={
                    selectedEventInfo?.promoTacticsIdNamePair?.map((p: promoTacticIdNamePair) => ({
                        key: p.id,
                        label: p.name,
                    })) || []
                }
                activeTab={promoActiveTab}
                setActiveTab={onPromoActiveTabChange}
                showAddButton
                isViewMode={disabled || isTpmTab}
                onAddBtnClick={onAddPromoBtnClick}
                // isAddDisabled={false}
                // todo remove the condition
                isAddDisabled={
                    (!!selectedEventInfo?.promoTacticsIdNamePair?.length && !isValid) || // if current form is invalid
                    !!selectedEventInfo?.currentPromoTactic?.hasChanges // if current form has changes then disable it
                }
                handleTabDelete={handlePromoDelete}
                isDeleteDisabled={
                    !deleteAllowedStatus.includes(selectedEventInfo?.currentPromoTactic?.status?.toLowerCase())
                }
                deleteBtnDisabledTooltip={`Delete is allowed only when the status is ${deleteAllowedStatus
                    .map((item: string) => formatWord(item))
                    .join(', ')}.`}
            />
            {!selectedEventInfo?.promoTacticsIdNamePair?.length ? (
                <div className=" flex-1 border border-backgroundGreyAlt6  bg-backgroundGreyAlt6  rounded p-3   flex justify-center items-center  ">
                    Please click on Add Promo button to add promo.
                </div>
            ) : (
                <div className="flex-1 overflow-hidden">
                    {activeTabPromoDetailsLoading || eventDataLoading ? (
                        <div className="h-full border border-backgroundGreyAlt6  bg-backgroundGreyAlt6  rounded p-3  flex  justify-center items-center  ">
                            <Spin />
                        </div>
                    ) : (
                        <div className="h-full overflow-hidden  ">
                            {/* PROMO COMPONENT */}
                            <div className="h-full flex flex-col  border border-backgroundGreyAlt6  rounded p-3  bg-white gap-2">
                                <PromoInfoSection
                                    fetchMetricsTriggeredFrom={fetchMetricsTriggeredFrom}
                                    setFetchMetricsTriggeredFrom={setFetchMetricsTriggeredFrom}
                                    mechanicType={mechanicType}
                                    eventId={eventId}
                                />
                                <PromoMetricDetails
                                    setFetchMetricsTriggeredFrom={setFetchMetricsTriggeredFrom}
                                    mechanicType={mechanicType}
                                />
                            </div>
                        </div>
                    )}
                </div>
            )}
            <ConfirmationModal
                open={!!deletePromoModalInfo?.open}
                onCancel={() => {
                    setDeletePromoModalInfo((prev) => ({ ...prev, open: false }));
                }}
                onSubmitClick={onPromoDelete}
                okText={deleteLoading ? <Spin indicator={antIconDanger} /> : 'Delete'}
                okButtonProps={{ type: 'ghost', danger: true, style: { borderRadius: 6 } }}
                loading={deleteLoading}
                subTitle={`Are you sure you want to delete ${deletePromoModalInfo?.name}?`}
                title="Delete Promo"
                description={`This will delete ${deletePromoModalInfo?.name} from the event`}
            />
        </>
    );
};

export default Promos;
